import { Link } from "react-router-dom";
import { ContentPageLayout } from "@/components/seo/ContentPageLayout";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { ComparisonTable } from "@/components/seo/ComparisonTable";

const BASE = "https://sorvaine.com";

const faqItems = [
  { question: "Is Sorvaine better than Loopio?", answer: "Sorvaine is better for teams that want fast AI-generated drafts without lengthy setup. Loopio is better for large enterprises with an existing content library." },
  { question: "How much does Loopio cost?", answer: "Loopio uses custom enterprise pricing. Contact them directly for a quote." },
  { question: "Can I switch from Loopio to Sorvaine?", answer: "Yes. Sorvaine doesn't require content migration — you can start immediately by uploading an RFP." },
];

const comparisonRows = [
  { feature: "Time to first draft", sorvaine: "Minutes", competitor: "Days (after setup)" },
  { feature: "Setup required", sorvaine: "None", competitor: "Weeks of library building" },
  { feature: "AI content generation", sorvaine: true, competitor: false },
  { feature: "AI content search", sorvaine: true, competitor: true },
  { feature: "Compliance mapping", sorvaine: true, competitor: true },
  { feature: "Auto-building knowledge base", sorvaine: true, competitor: false },
  { feature: "Enterprise CRM integrations", sorvaine: "Roadmap", competitor: true },
  { feature: "Best for", sorvaine: "Speed & AI drafts", competitor: "Library management" },
];

export default function LoopioAlternatives() {
  return (
    <ContentPageLayout
      title="Loopio Alternative — AI RFP Software | Sorvaine"
      description="Looking for a Loopio alternative? Sorvaine helps bid managers respond to RFPs faster with AI-powered proposal generation."
      canonical={`${BASE}/loopio-alternatives`}
      breadcrumbs={[
        { name: "Home", url: BASE },
        { name: "Loopio Alternatives", url: `${BASE}/loopio-alternatives` },
      ]}
    >
      <SchemaMarkup type="faq" items={faqItems} />

      <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
        Loopio Alternatives
      </h1>
      <p className="mt-4 text-lg text-white/50 max-w-[620px]">
        Loopio is a well-known RFP platform, but it's not the right fit for every team. Here's how the alternatives compare — and why teams are choosing Sorvaine.
      </p>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Why teams look for Loopio alternatives</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Loopio requires significant upfront investment. You need to populate a content library before the tool delivers value. Its AI is search-based — it finds existing answers but doesn't write new ones. For teams that want to move fast without weeks of setup, that's a dealbreaker.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Sorvaine vs Loopio</h2>
        <p className="mt-3 mb-6 text-white/50 leading-relaxed">
          The fundamental difference: Loopio is a content library with search. <Link to="/rfp-response-software" className="text-primary hover:underline">Sorvaine</Link> is an AI writing platform that builds your library as you work.
        </p>
        <ComparisonTable competitorName="Loopio" rows={comparisonRows} />
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Who should choose Sorvaine</h2>
        <ul className="mt-4 space-y-2.5 text-sm text-white/50">
          <li className="flex items-start gap-2"><span className="text-primary font-bold">→</span> Teams that want value from day one, without weeks of content migration</li>
          <li className="flex items-start gap-2"><span className="text-primary font-bold">→</span> Organisations that need AI-generated first drafts, not just content search</li>
          <li className="flex items-start gap-2"><span className="text-primary font-bold">→</span> Small to mid-sized proposal teams that prioritise speed</li>
          <li className="flex items-start gap-2"><span className="text-primary font-bold">→</span> Teams responding to diverse RFPs where reuse alone isn't enough</li>
        </ul>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Pricing comparison</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Loopio uses custom enterprise pricing — typically a five-figure annual commitment with onboarding fees, implementation services, and per-seat add-ons layered on top. Most teams sign a multi-year contract before they've shipped a single response through the platform.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          Sorvaine is transparent and self-serve. Plans start in the low hundreds per month, billed monthly, with no implementation fees and no minimum commitment. You can run a real bid through the platform on day one and decide whether to continue at the end of the month.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          For teams responding to fewer than 50 RFPs per year, Sorvaine typically costs 5-10x less than a comparable Loopio deployment — without sacrificing the AI capabilities that actually move the needle on win rate. <Link to="/resources/best-rfp-software" className="text-primary hover:underline">See our full pricing breakdown</Link>.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Who should switch from Loopio</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Loopio remains a credible choice for very large enterprises with a mature content library, dedicated knowledge managers, and the budget for ongoing curation. If that's you, Loopio works.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          You should consider switching to Sorvaine if any of the following apply: your team has under 30 contributors; your content library is more aspiration than reality; your bid manager spends more time chasing answers than writing them; your win rate has plateaued; or your AI is "smart search" but can't actually draft a response.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          The switch itself is low-risk. Sorvaine doesn't require you to migrate your existing content — the platform learns from each response you submit, so your library builds in parallel without a months-long data project. Most teams run Sorvaine alongside Loopio for one or two bids before fully cutting over.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Three scenarios where Sorvaine wins</h2>
        <div className="mt-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-white/80">1. The mid-market consultancy chasing 20+ tenders a year</h3>
            <p className="mt-2 text-sm text-white/40 leading-relaxed">
              A 40-person professional services firm bidding for public sector and enterprise work. They tried Loopio for six months, got 30% of their content into the library, and still had bid managers writing from scratch. With Sorvaine, they shipped their first AI-drafted response in week one and cut average response time from 35 hours to 12.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white/80">2. The agency with no central content library</h3>
            <p className="mt-2 text-sm text-white/40 leading-relaxed">
              A digital agency where every bid manager hoarded their own past responses in personal Google Drives. Loopio would have required a 3-month content audit before launch. Sorvaine generates first drafts directly from the RFP and the agency's website, then captures every approved response into a structured library — no audit required.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white/80">3. The bid team that switched after a single missed deadline</h3>
            <p className="mt-2 text-sm text-white/40 leading-relaxed">
              An EPC contractor missed a major ADNOC submission because their Loopio search returned 40 partial matches and their writers couldn't reconcile them in time. They moved to Sorvaine, which structures the response around the buyer's exact evaluation criteria and prevents "ready to submit" until every requirement is addressed.
            </p>
          </div>
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Other alternatives to Loopio</h2>
        <ul className="mt-4 space-y-3 text-sm text-white/50">
          <li><strong className="text-white/70">RFPIO (Responsive)</strong> — enterprise-grade with broader scope, but similar setup requirements. <Link to="/rfpio-alternatives" className="text-primary hover:underline">Compare →</Link></li>
          <li><strong className="text-white/70">Qvidian</strong> — document automation focused, less modern interface.</li>
          <li><strong className="text-white/70">Proposify</strong> — better for sales proposals than complex RFPs.</li>
        </ul>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">FAQ</h2>
        <div className="mt-6 space-y-6">
          {faqItems.map((item, i) => (
            <div key={i}>
              <h3 className="text-sm font-semibold text-white/70">{item.question}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Related</h2>
        <ul className="mt-4 space-y-2">
          <li><Link to="/best-rfp-software" className="text-sm text-primary hover:underline">Best RFP Software in 2026</Link></li>
          <li><Link to="/resources/what-is-rfp-response-software" className="text-sm text-primary hover:underline">What Is RFP Response Software?</Link></li>
        </ul>
      </section>
    </ContentPageLayout>
  );
}
